import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import { SandboxModeBanner } from '@/header/SandboxModeBanner.molecule';
import { addWorkbook } from '@/homescreen/homescreen.actions';
import { HOME_SCREEN_TABS } from '@/main/app.constants';
import { getWorksheetLink, goToWithReload } from '@/main/routing.utilities';
import { setShowModal } from '@/reportEditor/pdfExport.actions';
import { canModifyWorkbook as authServiceCanModifyWorkbook, isTrustedUser } from '@/services/authorization.service';
import { createUniqueCopyName, getTabFolder } from '@/utilities/homescreen.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { Icon } from '@seeqdev/qomponents';
import type { StoreWorkbookWithWorksheets } from '@/worksheet/worksheet.types';

interface ViewModeWorksheetActionsIF {
  isViewMode: boolean;
  isEditMode: boolean;
  isLocked: boolean;
}

export const ViewModeWorksheetActions = (props: ViewModeWorksheetActionsIF) => {
  const { isViewMode, isLocked } = props;

  const { t } = useTranslation();
  const [isCopying, setIsCopying] = useState(false);

  const { name, owner, effectivePermissions } = useFluxPaths(sqWorkbookStore, [
    'name',
    'owner',
    'effectivePermissions',
  ]);
  const workbook = {
    name,
    owner,
    effectivePermissions,
  };

  const canModifyWorkbook = authServiceCanModifyWorkbook(workbook);
  const isTopic = useFluxPath(sqWorkbookStore, (store) => store.isTopic);
  const isVantage = useFluxPath(sqWorkbookStore, (store) => store.isVantage);
  const isAnalysis = useFluxPath(sqWorkbookStore, (store) => store.isAnalysis);
  const workbookId = useFluxPath(sqWorkbookStore, (store) => store.workbookId);
  const isTrusted = useFluxPath(sqWorkbenchStore, (store) => store.currentUser.isTrusted);
  const navigate = useNavigate();

  const onClickBadge = () => {
    // We force a page load rather than routing. If we don't do this, state from view only mode can leak into edit mode,
    // unintentionally changing a workstep
    goToWithReload(getWorksheetLink(workbookId, sqWorkbenchStore.stateParams.worksheetId));
  };

  const duplicateToMyFolder = async () => {
    if (!isTrustedUser() || isCopying || !workbookId) return;

    setIsCopying(true);

    try {
      const myFolderTab: { id: string } | null = await getTabFolder(HOME_SCREEN_TABS.MY_FOLDER);
      if (!myFolderTab?.id) {
        throw new Error('My Folder not available');
      }

      const currentWorksheetId = sqWorkbenchStore.stateParams.worksheetId;
      const currentWorksheetIndex = sqWorkbookStore.worksheets.findIndex(
        (worksheet) => worksheet.worksheetId === currentWorksheetId,
      );

      const duplicate: StoreWorkbookWithWorksheets = await addWorkbook({
        name: createUniqueCopyName(name),
        branchFrom: workbookId,
        isTopic,
        isVantage,
        folderId: myFolderTab.id,
      });

      const duplicateWorksheetId =
        currentWorksheetIndex >= 0 ? duplicate.worksheets?.[currentWorksheetIndex]?.worksheetId : undefined;
      navigate(getWorksheetLink(duplicate.id, duplicateWorksheetId));
      requestAnimationFrame(() =>
        successToast({
          messageKey: 'SANDBOX_MODE.SUCCESS_MESSAGE',
          messageParams: { newWorkbook: duplicate.name },
        }),
      );
    } catch (error: unknown) {
      errorToast({ httpResponseOrError: error, displayForbidden: true });
    } finally {
      setIsCopying(false);
    }
  };

  const badgeTooltip = canModifyWorkbook ? 'VIEWING_ONLY.TOOLTIP' : 'VIEWING_ONLY.TOOLTIP_NO_ACCESS';

  return isViewMode ? (
    <div
      className="cursorPointer flexColumnContainer tw:ml-sq-10 tw:flex-none tw:justify-end tw:self-stretch"
      data-testid="viewing-only-badge">
      <div className="viewing-badge width-maximum height-maximum flexColumnContainer tw:items-center tw:justify-center">
        <HoverTooltip text={badgeTooltip} placement="bottom">
          <span className="tw:p-sq-3">{t('VIEWING_ONLY.HEADER')}</span>
        </HoverTooltip>
      </div>
      <SandboxModeBanner />
      {isAnalysis && isTrusted && (
        <div className="tw:p-sq-3" onClick={duplicateToMyFolder}>
          <HoverTooltip text={t('VIEWING_ONLY.COPY_TO_MY_FOLDER')} placement="bottom">
            <IconWithSpinner
              icon="fa-copy"
              spinning={isCopying}
              testId="viewOnlyCopyIcon"
              type="white"
              large={true}
              extraClassNames={clsx([
                'sq-navbar-default tw:px-0.5',
                isCopying ? 'tw:cursor-not-allowed' : 'tw:cursor-pointer',
              ])}
            />
          </HoverTooltip>
        </div>
      )}

      {canModifyWorkbook && !isLocked && (
        <div className="tw:p-sq-3" onClick={onClickBadge}>
          <Icon
            icon="fa-pencil"
            tooltip={t('VIEWING_ONLY.EDIT')}
            tooltipPlacement="bottom"
            size="lg"
            type="theme-light"
            testId="viewOnlyEditIcon"
            extraClassNames="sq-navbar-default tw:cursor-pointer"
          />
        </div>
      )}
      {isTopic && (
        <span className="cursorPointer sq-navbar-default link-no-underline tw:p-sq-3">
          <Icon
            extraClassNames="mr7 mt3"
            icon="fc-pdf-export"
            tooltip={t('REPORT.EDITOR.SAVE_AS_PDF_BUTTON_TITLE')}
            tooltipPlacement="bottom"
            type="theme-light"
            large={true}
            onClick={() => setShowModal(true)}
            testId="pdfExportIcon"
          />
        </span>
      )}
    </div>
  ) : null;
};
